import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  MapPin, 
  Heart,
  Share2, 
  Copy, 
  Mail, 
  MessageCircle,
  Check,
  List,
  FolderPlus,
  Tag,
  X
} from "lucide-react";

import { BACKEND_URL } from "@/config/runtime";
import { VendorPlaceholder } from "@/components/VendorPlaceholder";
import { trackView } from "@/utils/viewTracker";
import { getResponsiveSrc, handleImageError } from "@/utils/responsiveImage";

// List colors for custom lists
const LIST_COLORS = [
  { value: "#6B8E6B", label: "Forest Green" },
  { value: "#FF6B35", label: "Orange" },
  { value: "#5BA4D9", label: "Blue" },
  { value: "#9C27B0", label: "Purple" },
  { value: "#E91E63", label: "Pink" },
  { value: "#607D8B", label: "Gray" },
];

// Save to List Modal Component
const SaveToListModal = ({ open, onClose, onSave, lists, onCreateList }) => {
  const [selectedListId, setSelectedListId] = useState(null);
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [newListColor, setNewListColor] = useState("#6B8E6B");
  const [creating, setCreating] = useState(false);

  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    setCreating(true);
    const newList = await onCreateList(newListName.trim(), newListColor);
    if (newList) {
      setSelectedListId(newList.list_id);
      setShowCreateNew(false);
      setNewListName("");
    }
    setCreating(false);
  };

  const handleSave = () => {
    onSave(selectedListId);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#E53935] fill-current" />
            Save to List
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {/* No list option */}
          <button
            onClick={() => setSelectedListId(null)}
            className={`w-full p-3 rounded-lg border-2 transition-all flex items-center gap-3 ${
              selectedListId === null 
                ? 'border-[#6B8E6B] bg-green-50' 
                : 'border-stone-200 hover:border-stone-300'
            }`}
          >
            <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center">
              <Heart className="w-5 h-5 text-stone-700" />
            </div>
            <div className="text-left flex-1">
              <p className="font-medium text-[#1E3A2F]">All Saved</p>
              <p className="text-xs text-stone-700">Save without a specific list</p>
            </div>
            {selectedListId === null && (
              <Check className="w-5 h-5 text-[#1A3D2A]" />
            )}
          </button>

          {/* Existing lists */}
          {lists.map((list) => (
            <button
              key={list.list_id}
              onClick={() => setSelectedListId(list.list_id)}
              className={`w-full p-3 rounded-lg border-2 transition-all flex items-center gap-3 ${
                selectedListId === list.list_id 
                  ? 'border-[#6B8E6B] bg-green-50' 
                  : 'border-stone-200 hover:border-stone-300'
              }`}
            >
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: list.color + '20' }}
              >
                <List className="w-5 h-5" style={{ color: list.color }} />
              </div>
              <div className="text-left flex-1">
                <p className="font-medium text-[#1E3A2F]">{list.name}</p>
                <p className="text-xs text-stone-700">{list.vendor_count || 0} vendors</p>
              </div>
              {selectedListId === list.list_id && (
                <Check className="w-5 h-5 text-[#1A3D2A]" />
              )}
            </button>
          ))}

          {/* Create new list */}
          {showCreateNew ? (
            <div className="p-4 rounded-lg border-2 border-dashed border-[#6B8E6B] bg-green-50/50 space-y-3">
              <Input
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder="List name..."
                className="bg-white"
                autoFocus
              />
              <div className="flex gap-2">
                {LIST_COLORS.map((c) => (
                  <button
                    key={c.value}
                    onClick={() => setNewListColor(c.value)}
                    className={`w-7 h-7 rounded-full transition-transform ${
                      newListColor === c.value ? 'ring-2 ring-offset-2 ring-[#4A6B4A] scale-110' : ''
                    }`}
                    style={{ backgroundColor: c.value }}
                    title={c.label}
                  />
                ))}
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowCreateNew(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleCreateList}
                  disabled={!newListName.trim() || creating}
                  className="flex-1 btn-primary"
                >
                  {creating ? "Creating..." : "Create"}
                </Button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowCreateNew(true)}
              className="w-full p-3 rounded-lg border-2 border-dashed border-stone-300 hover:border-[#6B8E6B] transition-all flex items-center gap-3 text-stone-700 hover:text-[#1A3D2A]"
            >
              <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center">
                <FolderPlus className="w-5 h-5" />
              </div>
              <span className="font-medium">Create New List</span>
            </button>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleSave} className="flex-1 btn-primary">
              Save Vendor
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Share Menu Component
const ShareMenu = ({ listing, children }) => {
  const shareUrl = `${window.location.origin}/listing/${listing.slug}`;
  const shareTitle = listing.title;
  const shareText = `Check out ${listing.title} on CampYid - Plan Smarter Camp Better`;
  const touchStartY = useRef(null);
  const [open, setOpen] = useState(false);

  const mailtoUrl = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`;

  const handleEmailShare = () => {
    const a = document.createElement('a');
    a.href = mailtoUrl;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleCopyLink = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  const handleWhatsAppShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleNativeShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error("Failed to share");
        }
      }
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild
        onTouchStart={(e) => { touchStartY.current = e.touches[0].clientY; }}
        onTouchEnd={(e) => {
          const dy = Math.abs((e.changedTouches[0]?.clientY || 0) - (touchStartY.current || 0));
          if (dy > 10) { e.preventDefault(); setOpen(false); }
          touchStartY.current = null;
        }}
        onClick={(e) => e.preventDefault()}
      >
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={handleCopyLink} className="cursor-pointer">
          <Copy className="w-4 h-4 mr-2" />
          Copy Link
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleEmailShare} className="cursor-pointer" data-testid="share-email-btn">
          <Mail className="w-4 h-4 mr-2" />
          Email
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleWhatsAppShare} className="cursor-pointer">
          <MessageCircle className="w-4 h-4 mr-2" />
          WhatsApp
        </DropdownMenuItem>
        {navigator.share && (
          <DropdownMenuItem onClick={handleNativeShare} className="cursor-pointer">
            <Share2 className="w-4 h-4 mr-2" />
            More Options
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Main ListingCard Component
export const ListingCard = React.memo(({ listing, variant = "default", onQuickView, isPromotedBySubscription = null, savedListings = null, vendorListsCache = null, hasDeal = false, dealTitle = null }) => {
  const { user, isAuthenticated } = useAuth();
  const [isSaved, setIsSaved] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [vendorLists, setVendorLists] = useState([]);
  const cardRef = useRef(null);
  const hasTrackedImpression = useRef(false);
  
  // Use flyer_image if available, otherwise first gallery image
  const displayImage = listing.flyer_image || listing.gallery_images?.[0];
  const hasImage = !!displayImage;
  const focalPoint = listing.flyer_focal_point || { x: 50, y: 50 };
  const displayMode = listing.flyer_display_mode || "focal_point"; // "focal_point" or "fit_to_view"
  
  // Custom border styling for listings with active border subscription
  const hasPremiumBorder = listing.has_custom_border && listing.custom_border_color;
  const borderStyle = hasPremiumBorder 
    ? { 
        border: `6px solid ${listing.custom_border_color}`,
        boxShadow: `0 0 25px ${listing.custom_border_color}50, 0 4px 6px -1px rgba(0, 0, 0, 0.1)`,
        '--border-color': listing.custom_border_color
      }
    : {};

  // Track impression when card becomes visible on screen
  useEffect(() => {
    if (!listing?.listing_id || hasTrackedImpression.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedImpression.current) {
            trackView(listing.listing_id);
            hasTrackedImpression.current = true;
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );
    
    if (cardRef.current) {
      observer.observe(cardRef.current);
    }
    
    return () => observer.disconnect();
  }, [listing?.listing_id]);

  // Check if listing is saved - use cached data if available to avoid N API calls
  useEffect(() => {
    if (!isAuthenticated || !listing) return;
    
    // If parent passed cached saved listings, use that instead of making API call
    if (savedListings !== null) {
      const found = savedListings.find(s => s.listing_id === listing.listing_id);
      if (found) {
        setIsSaved(true);
        setSavedId(found.saved_id);
      } else {
        setIsSaved(false);
        setSavedId(null);
      }
      return;
    }
    
    // Fallback: fetch individually (for standalone card usage)
    const checkSaved = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/saved-listings`, {
          credentials: "include"
        });
        if (res.ok) {
          const saved = await res.json();
          const found = saved.find(s => s.listing_id === listing.listing_id);
          if (found) {
            setIsSaved(true);
            setSavedId(found.saved_id);
          }
        }
      } catch (error) {
        console.error("Error checking saved:", error);
      }
    };

    checkSaved();
  }, [isAuthenticated, listing, savedListings]);

  // Handle card click - use quick view if available, otherwise navigate
  const handleCardClick = (e) => {
    if (onQuickView) {
      e.preventDefault();
      onQuickView(listing.slug);
    }
  };

  // Use cached vendor lists if available, otherwise fetch once
  useEffect(() => {
    if (!isAuthenticated) return;
    
    // If parent passed cached lists, use that
    if (vendorListsCache !== null) {
      setVendorLists(vendorListsCache);
      return;
    }
    
    // Fallback: fetch individually
    const fetchLists = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/camp/lists`, {
          credentials: "include"
        });
        if (res.ok) {
          setVendorLists(await res.json());
        }
      } catch (error) {
        console.error("Error fetching lists:", error);
      }
    };
    fetchLists();
  }, [isAuthenticated, vendorListsCache]);

  const handleSaveClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error("Please sign in to save listings");
      return;
    }
    
    if (isSaved) {
      handleUnsave();
    } else {
      setShowSaveModal(true);
    }
  };

  const handleUnsave = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/saved-listings/${savedId}`, {
        method: "DELETE",
        credentials: "include"
      });
      setIsSaved(false);
      setSavedId(null);
      toast.success("Removed from saved listings");
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  const handleSaveToList = async (listId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/saved-listings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          listing_id: listing.listing_id,
          list_id: listId 
        })
      });
      const data = await res.json();
      setIsSaved(true);
      setSavedId(data.saved_id);
      toast.success(listId ? "Added to list!" : "Added to saved listings!");
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  const handleCreateList = async (name, color) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/camp/lists`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name, color })
      });
      if (res.ok) {
        const newList = await res.json();
        setVendorLists(prev => [...prev, newList]);
        toast.success("List created!");
        return newList;
      }
    } catch (error) {
      toast.error("Failed to create list");
    }
    return null;
  };

  // Show promoted badge only based on active subscription status passed from parent,
  // or fall back to variant for homepage. Never use legacy is_promoted field.
  const isPromoted = isPromotedBySubscription !== null 
    ? isPromotedBySubscription 
    : variant === "promoted";

  return (
    <>
      <div ref={cardRef} className={`listing-card group relative ${hasPremiumBorder ? 'premium-border-shimmer' : ''}`} style={borderStyle}>
        <Link
          to={`/listing/${listing.slug}`}
          className="block"
          onClick={handleCardClick}
          data-testid={`listing-card-${listing.listing_id}`}
        >
          <div className="relative h-48 overflow-hidden" style={{ backgroundColor: displayMode === 'fit_to_view' ? 'var(--card-bg, #f5f5f0)' : undefined }}>
            {hasImage ? (
              <img
                src={getResponsiveSrc(displayImage)}
                onError={(e) => handleImageError(e, displayImage)}
                alt={listing.title}
                className={`w-full h-full transform group-hover:scale-105 transition-transform duration-500 ${
                  displayMode === 'fit_to_view' ? 'object-contain' : 'object-cover'
                }`}
                style={displayMode === 'fit_to_view' ? {} : { objectPosition: `${focalPoint.x}% ${focalPoint.y}%` }}
                referrerPolicy="no-referrer"
                loading="lazy"
                sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                width={400}
                height={192}
              />
            ) : (
              <VendorPlaceholder name={listing.title} size="default" />
            )}
            {isPromoted && (
              <div className="absolute top-3 left-3">
                <span className="badge-promo">⭐ Promoted</span>
              </div>
            )}
            {hasDeal && (
              <div className={`absolute ${isPromoted ? "top-10" : "top-3"} left-3`}>
                <span className="bg-[#22C55E] text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide shadow-sm">Deal</span>
              </div>
            )}
            {listing.target_genders?.length > 0 && (
              <div className="absolute top-3 right-3">
                <span className="bg-white/90 text-[#1E3A2F] text-xs font-medium px-2 py-1 rounded-full">
                  {listing.target_genders.join(" & ")}
                </span>
              </div>
            )}
          </div>
          <div className="p-4">
            <h3 className="font-bold text-[#1E3A2F] mb-1 group-hover:text-[#1A3D2A] transition-colors line-clamp-1">
              {listing.title}
            </h3>
            {dealTitle && (
              <div className="mb-2 flex items-center gap-1.5" data-testid={`deal-title-${listing.listing_id}`}>
                <Tag className="w-3 h-3 text-[#22C55E] shrink-0" />
                <span className="text-xs font-semibold text-[#22C55E] line-clamp-1">{dealTitle}</span>
              </div>
            )}
            <p className="text-sm text-[#1A3D2A] line-clamp-2 mb-3">{listing.description}</p>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 text-xs text-[#1A3D2A]">
                <MapPin className="w-3 h-3" />
                {/* Location display logic:
                    - "Anywhere" if service_areas includes "anywhere" or no location data
                    - For trips category: show physical locations (Multiple if >1, else city name)
                    - For other categories: show service areas (Multiple if >1, else area name)
                */}
                {listing.service_areas?.some(area => area.toLowerCase() === 'anywhere') || 
                 (!listing.locations?.length && !listing.service_areas?.length)
                  ? "Anywhere"
                  : listing.category_id === 'cat_b3e6e1ecfb83' || listing.category_selections?.some(s => s.category_id === 'cat_b3e6e1ecfb83')
                    ? (listing.locations?.length > 1
                        ? "Multiple"
                        : listing.locations?.[0]?.city || listing.locations?.[0]?.name || listing.service_areas?.[0] || "Anywhere")
                    : (listing.service_areas?.length > 1
                        ? "Multiple"
                        : listing.service_areas?.[0] || listing.locations?.[0]?.city || "Anywhere")}
              </div>
              {listing.price_range && (
                <span className="text-xs font-semibold text-[#1A3D2A]">{listing.price_range}</span>
              )}
            </div>
            {listing.target_age_groups?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {listing.target_age_groups.slice(0, 2).map((age) => (
                  <span key={age} className="text-xs bg-green-50 text-[#1A3D2A] px-2 py-0.5 rounded-full">
                    {age}
                  </span>
                ))}
                {listing.target_age_groups.length > 2 && (
                  <span className="text-xs text-[#1A3D2A]">+{listing.target_age_groups.length - 2}</span>
                )}
              </div>
            )}
          </div>
        </Link>
        
        {/* Action buttons overlay */}
        <div className="absolute bottom-4 right-4 flex gap-2 z-10">
          <ShareMenu listing={listing}>
            <button
              className="w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors"
              title="Share"
            >
              <Share2 className="w-4 h-4 text-[#1A3D2A]" />
            </button>
          </ShareMenu>
          <button
            onClick={handleSaveClick}
            className={`w-8 h-8 rounded-full shadow-md flex items-center justify-center transition-colors ${
              isSaved 
                ? 'bg-white hover:bg-gray-50' 
                : 'bg-white hover:bg-gray-50'
            }`}
            title={isSaved ? "Saved" : "Save"}
          >
            {isSaved ? (
              <Heart className="w-4 h-4 text-[#E53935] fill-current" />
            ) : (
              <Heart className="w-4 h-4 text-[#1A3D2A]" />
            )}
          </button>
        </div>
      </div>

      {/* Save to List Modal */}
      <SaveToListModal
        open={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveToList}
        lists={vendorLists}
        onCreateList={handleCreateList}
      />
    </>
  );
});

// Export ShareMenu for use in ListingPage
export { ShareMenu };

export default ListingCard;
